// Formas de pagamento como a tela mostra. A regra (pedido em duas partes vale pelas formas das partes,
// não pela forma escolhida na criação) mora no servidor, em backend/services/formas_pagamento.py, que
// manda `formas_pagamento` em cada pedido da lista. Aqui ficam os nomes, o estado de cada parte e o pagamento
// que o formulário de pedido e proposta manda.
import { tomDoLink } from './belluno';
import { diaDoCalendario, formatBRL } from './format';

export const DOIS_MEIOS = 'dois_meios';

// Financiamento: a financeira paga a BCMED de uma vez, depois da nota. A proposta passa pela análise do time de
// Financiamentos e vira pedido na aprovação (no nome do vendedor, direto ao Tiny); o Financeiro registra o
// recebimento. Sem link e sem comprovante do vendedor.
export const FINANCIAMENTO = 'financiamento';

// Formas pagas por link e confirmadas sozinhas (sem comprovante): o cartão pela Pagar.me e a Belluno pelo
// link dela. As parcelas escolhidas no pedido são o teto do link.
export const FORMAS_COM_LINK = ['cartao_credito', 'belluno'];

// Parcelas que cada link oferece, [menor, maior]: o cartão da Pagar.me de 1x a 12x; a Belluno só de 13x a 21x, acima
// do que o cartão já cobre (Matheus, 2026-10-08; o backend tem o mesmo intervalo em models.PARCELAS_BELLUNO).
const PARCELAS_DO_LINK = { cartao_credito: [1, 12], belluno: [13, 21] };
export const opcoesDeParcelas = (forma) => {
  const [menor, maior] = PARCELAS_DO_LINK[forma] || [1, 1];
  return Array.from({ length: maior - menor + 1 }, (_, i) => menor + i);
};

// [nome inteiro, nome curto]: o curto serve para duas formas juntas ("Cartão + PIX").
const NOMES = {
  cartao_credito: ['Cartão de crédito', 'Cartão'],
  pix: ['PIX', 'PIX'],
  boleto: ['Boleto', 'Boleto'],
  cartao_debito: ['Cartão de débito', 'Débito'],
  dinheiro: ['Dinheiro', 'Dinheiro'],
  transferencia: ['Transferência', 'Transferência'],
  belluno: ['Belluno', 'Belluno'],
  [FINANCIAMENTO]: ['Financiamento', 'Financiamento'],
};
const FORMAS_DO_PEDIDO = Object.keys(NOMES);

// Opções do filtro, na ordem das formas do ERP; as chaves são os valores que o GET /pedidos aceita em `forma`.
export const OPCOES_FORMA = {
  ...Object.fromEntries(FORMAS_DO_PEDIDO.map((forma) => [forma, { label: NOMES[forma][0] }])),
  [DOIS_MEIOS]: { label: 'Dois meios de pagamento' },
};

// Formas que fecham pelo comprovante que o vendedor anexa e o financeiro confere: as do pedido sem link. O
// financiamento fecha pelo recebimento que o Financeiro registra.
export const FORMAS_MANUAIS = FORMAS_DO_PEDIDO.filter((f) => !FORMAS_COM_LINK.includes(f) && f !== FINANCIAMENTO);

// O select do formulário de pedido e proposta, nesta ordem; financiamento só aparece na proposta (o pedido de
// financiamento nasce na aprovação).
export const FORMAS_DO_FORMULARIO = [
  'pix', 'boleto', 'cartao_credito', 'cartao_debito', 'dinheiro', 'transferencia', 'belluno', FINANCIAMENTO,
];

export const nomeDaForma = (forma) => NOMES[forma]?.[0] || forma;

// Uma API antiga, sem `formas_pagamento`, ainda tem a forma do pedido.
export const formasDoPedido = (p) => p?.formas_pagamento ?? (p?.forma_pagamento ? [p.forma_pagamento] : []);

export const rotuloFormas = (p) => {
  const formas = formasDoPedido(p);
  if (formas.length === 1) return nomeDaForma(formas[0]);
  return formas.map((f) => NOMES[f]?.[1] || f).join(' + ');
};

// No meio da frase: "cartão + PIX" (sigla e nome próprio ficam como estão).
const NOMES_PROPRIOS = ['PIX', 'Belluno'];
export const emMinusculas = (texto) =>
  texto.split(' + ')
    .map((t) => (NOMES_PROPRIOS.some((n) => t.startsWith(n)) ? t : t.charAt(0).toLowerCase() + t.slice(1)))
    .join(' + ');

// O pagamento que o formulário manda. Só as formas com link e o financiamento têm parcelas; só o
// financiamento tem valor de parcela, que é o da financeira (com juros) e não sai do total dividido.
export const camposDePagamento = (forma, parcelas, valorParcela) => ({
  forma_pagamento: forma,
  parcelamento: [...FORMAS_COM_LINK, FINANCIAMENTO].includes(forma) ? (parseInt(parcelas, 10) || 1) : 1,
  valor_parcela: forma === FINANCIAMENTO ? (Number(valorParcela) || null) : null,
});

// Método da parte (nome da Pagar.me, e "belluno" desde 2026-10-16) para a forma do ERP; parte de link antigo não tem
// método e é cartão.
const FORMA_DA_PARTE = { credit_card: 'cartao_credito', pix: 'pix', boleto: 'boleto', belluno: 'belluno' };
export const formaDaParte = (parte) => FORMA_DA_PARTE[parte.metodo || 'credit_card'] || parte.metodo;
// O caminho de volta: o método com que cada parte nasce herda a forma do pedido (cartão, PIX, boleto ou Belluno).
const METODO_DA_FORMA = Object.fromEntries(Object.entries(FORMA_DA_PARTE).map(([metodo, forma]) => [forma, metodo]));
export const metodoDaForma = (forma) => METODO_DA_FORMA[forma];

// O antifraude da Pagar.me reprovou a última tentativa? O webhook guarda a Order em pagarme_orders_reprovadas e cada
// tentativa nova entra no fim de pagarme_order_ids, então o aviso some sozinho quando o cliente tenta de novo. No
// pagamento em duas partes cada parte guarda a Order da própria última tentativa (pagamento_split[].pagarme_order_id).
export const ultimaTentativaReprovada = (p) => {
  const reprovadas = p?.pagarme_orders_reprovadas || [];
  if (p?.pagamento_split?.length) {
    return p.pagamento_split.some((parte) => !parte.pago && reprovadas.includes(parte.pagarme_order_id));
  }
  const ultima = (p?.pagarme_order_ids || []).slice(-1)[0];
  return Boolean(ultima) && reprovadas.includes(ultima);
};

// Etapa do financiamento da proposta, a mesma regra de services/propostas_service.etapa_financiamento: null se a
// forma não é Financiamento; sem etapa gravada é "aguardando".
export const etapaFinanciamento = (proposta) =>
  (proposta?.dados?.forma_pagamento === FINANCIAMENTO ? (proposta.financiamento?.etapa || 'aguardando') : null);

// O que o time de Financiamentos pode fazer em cada etapa: services/propostas_service.TRANSICOES_FINANCIAMENTO lida
// ao contrário. Aprovado é final; negado volta à análise em outra financeira.
const PROXIMAS_ETAPAS = { aguardando: ['analise', 'aprovado', 'negado'], analise: ['aprovado', 'negado'], negado: ['analise'] };
export const proximasEtapas = (proposta) => PROXIMAS_ETAPAS[etapaFinanciamento(proposta)] || [];

// "BV Financeira, 24x de R$ 1.234,56": o `financiamento` da proposta ou do pedido; o que falta fica de fora.
export const condicaoFinanciamento = (f) => [
  f?.financeira,
  f?.parcelas && f?.valor_parcela ? `${f.parcelas}x de ${formatBRL(f.valor_parcela)}` : null,
].filter(Boolean).join(', ');

// "Financiamento: BV Financeira, 24x de R$ 1.234,56": o pagamento do pedido de financiamento nas telas.
export const resumoFinanciamento = (pedido) =>
  [nomeDaForma(FINANCIAMENTO), condicaoFinanciamento(pedido?.financiamento)].filter(Boolean).join(': ');

// Pedido de financiamento que a financeira ainda não pagou: a fila "Financiamentos a receber" do Financeiro.
export const aguardandoFinanceira = (pedido) => pedido?.forma_pagamento === FINANCIAMENTO
  && !['confirmed', 'paid', 'refunded'].includes(pedido.payment_status)
  && pedido.workflow_status !== 'cancelado';

// Tom do ponto do pedido (o do workflow, de components/WorkflowStatusChips): o financiamento já no Tiny e ainda não
// pago não está concluído (verde); anda como "Aguardando pagamento" (cobalto) até o Financeiro registrar o recebimento.
export const tomDoPedido = (pedido, tom) => (tom === 'ok' && aguardandoFinanceira(pedido) ? 'run' : tom);

// Dias de calendário (de quem olha) desde a aprovação do financiamento: 0 no mesmo dia.
export const diasDesdeAprovacao = (pedido, hoje = new Date()) => {
  const aprovado = new Date(pedido?.financiamento?.aprovado_em || NaN);
  if (Number.isNaN(aprovado.getTime())) return null;
  return Math.round((diaDoCalendario(hoje) - diaDoCalendario(aprovado)) / 86400000);
};

// Estado de uma parte do pagamento em duas partes, para o financeiro ver qual falta.
export const situacaoDaParte = (parte, pedido) => {
  if (parte.pago) return { texto: 'Pago', tom: 'ok' };
  // Parte Belluno se paga pelo link dela (sem comprovante): link vencido ou recusado pede um novo ao vendedor.
  if (parte.metodo === 'belluno') {
    const link = parte.belluno_link;
    if (link?.transaction_id && !link.aberto) return { texto: `Link ${String(link.status_label || 'encerrado').toLowerCase()}`, tom: tomDoLink(link) };
    return { texto: 'Aguardando pagamento', tom: 'run' };
  }
  const temComprovante = (pedido.comprovantes || []).some((c) => c.parte === parte.indice);
  if (!temComprovante) return { texto: 'Aguardando pagamento', tom: 'run' };
  if (pedido.workflow_status === 'rejeitado') return { texto: 'Comprovante rejeitado', tom: 'bad' };
  if (pedido.workflow_status === 'comprovante_em_analise') return { texto: 'Comprovante em análise', tom: 'att' };
  return { texto: 'Comprovante anexado', tom: 'att' };
};
